import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, ApiError } from '../lib/api-client';

export interface MspUser {
  userId: string;
  email: string;
  tenantId: string;
  tenantSlug: string;
  role: string;
}

interface AuthContextValue {
  user: MspUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const HUB_URL = (import.meta.env.VITE_HUB_URL as string | undefined) ?? 'https://hub.theonestack.com';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<MspUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await api.get<{ data: { id: string; email: string; tenantId: string; tenantSlug: string; orgRole: string } }>('/api/portal/auth/me');
      setUser({
        userId: data.data.id,
        email: data.data.email,
        tenantId: data.data.tenantId,
        tenantSlug: data.data.tenantSlug,
        role: data.data.orgRole,
      });
    } catch (err) {
      setUser(null);
      // If unauthenticated, redirect to Hub for SSO
      if (err instanceof ApiError && err.status === 401) {
        window.location.href = `${HUB_URL}/login?redirect=legal`;
      }
    }
  }, []);

  useEffect(() => {
    refresh().finally(() => setIsLoading(false));
  }, [refresh]);

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isLoading,
      refresh,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
